
import {
  Vue,
  Component,
  Prop,
  Ref,
  Model,
  Watch,
} from "vue-property-decorator";
import {
  ElementUploadFile,
  ElUpload,
  UploadResponse,
  FileInfo,
} from "./definitions";
import { getImageBlobData, downloadBlob, download } from "@/utils";
import { preview } from "@/utils/image";
import throttle from "lodash/throttle";
import { getFileSizeWithUnit, isImageUrl } from "./utils";
import { Message } from "element-ui";
import { getToken } from "@/service/ewold/api/user";
import { fileUploadHost, fileDownloadHost } from "@/config/host";
function showSizeExceedErrMsg(size: number) {
  return throttle(() => {
    Message.error(`文件大小不能大于${getFileSizeWithUnit(size)}`);
  }, 300);
}
@Component({
  components: {},
})
export default class FileUploader extends Vue {
  /** 绑定值 */
  @Model("change", { type: Array, required: false }) readonly value?:
    | string[]
    | ElementUploadFile<UploadResponse>[];
  /** 是否自动上传 */
  @Prop({ required: false, default: true }) autoUpload?: boolean;
  /** 是否支持多选 */
  @Prop({ required: false, default: false }) multiple?: boolean;
  /** 接收类型 */
  @Prop({ required: false, default: "*" }) accept?: string;
  /** 是否支持下载 */
  @Prop({ required: false, default: false }) dowloadable?: boolean;
  /** 最大允许上传个数 */
  @Prop({ required: false }) limit?: number;
  /** 文件超出个数限制时的钩子 */
  @Prop({ required: false }) onExceed?: (
    files: File[],
    fileList: ElementUploadFile<UploadResponse>[]
  ) => void;
  /** 是否禁用 */
  @Prop({ required: false, default: false }) disabled?: boolean;
  /** 覆盖默认的上传行为，可以自定义上传的实现 */
  @Prop({ required: false }) httpRequest?: () => void;
  /** 单个文件大小限制(单位 byte)*/
  @Prop({ required: false }) maxSize?: number;
  @Ref("uploader") elUploader!: ElUpload<UploadResponse>;
  uploadHost = fileUploadHost;
  token = "";
  tokenLastFetch?: Date;
  fileList: string[] | ElementUploadFile<UploadResponse>[] = [];
  emitingChange = false;
  get uploadUrl() {
    return `${this.uploadHost}/fastdfsapi/upload`;
  }
  get showSizeExceedErrMsgFn() {
    if (typeof this.maxSize === "number") {
      return showSizeExceedErrMsg(this.maxSize);
    }
    return null;
  }
  @Watch("value", { immediate: true })
  handleValueChange(val: string[] | ElementUploadFile<UploadResponse>[]) {
    if (this.emitingChange || !(Array.isArray(val) && val.length)) {
      return;
    }
    if (typeof val[0] === "string") {
      const files = val as string[];
      this.fileList = files.map((ele: string) => {
        if (typeof ele === "string") {
          return {
            url: ele,
            name: ele,
            status: "success",
          };
        }
      }) as ElementUploadFile<UploadResponse>[];
    } else {
      this.fileList = val as ElementUploadFile<UploadResponse>[];
    }
  }
  mounted() {
    this.getToken();
  }
  async getToken() {
    this.tokenLastFetch = new Date();
    const res = await getToken();
    console.log(res);
    if (res.is_success && typeof res.result === "string") {
      this.token = res.result;
      return res.result;
    }
  }
  isUploadSuccess(file: ElementUploadFile<UploadResponse>) {
    const remoteUrl = this.getRemoteUrl(file);
    return !!remoteUrl;
  }
  getRemoteUrl(file: ElementUploadFile<UploadResponse>) {
    const res = file.response;
    if (Array.isArray(res?.Data) && res?.Data.length) {
      const path = res.Data[0];
      return path ? `${fileDownloadHost}/${path.replace(/^\/+/, "")}` : "";
    }
    return "";
  }
  async handleRemove(file: ElementUploadFile<UploadResponse>) {
    const index = this.elUploader.uploadFiles.findIndex(
      (ele) => ele.uid === file.uid
    );
    if (index > -1) {
      this.remove(index);
    }
  }
  remove(index: number) {
    this.elUploader.uploadFiles.splice(index, 1);
    this.triggerChange();
  }
  async handlePreview(file: ElementUploadFile<UploadResponse>) {
    if (isImageUrl(file.url)) {
      preview({ url: file.url });
    } else {
      download(file.url, file.name);
    }
  }
  handleUploadSuccess(
    response: UploadResponse,
    file: ElementUploadFile<UploadResponse>
  ) {
    const uploadSuccess =
      response.Success && Array.isArray(response.Data) && response.Data[0];
    if (!uploadSuccess) {
      file.status = "error";
    }
  }
  handleUploadError(err: unknown, file: ElementUploadFile<UploadResponse>) {
    this.$message.error(`文件"${file.name}"上传失败，请重试`);
  }
  async handleDownload(file: ElementUploadFile<UploadResponse>) {
    if (!file.url) {
      return;
    }
    if (file.url.startsWith("blob:")) {
      download(file.url, file.name);
    } else {
      const imgBlob = await getImageBlobData(file.url);
      downloadBlob(imgBlob, file.name);
    }
  }
  onChange(
    file: ElementUploadFile<UploadResponse>,
    fileList: ElementUploadFile<UploadResponse>[]
  ) {
    console.log("onChange file", file);
    const removeFile = () => {
      const index = fileList.findIndex((f) => f.uid === file.uid);
      fileList.splice(index, 1);
    };
    if (
      typeof this.maxSize === "number" &&
      file?.raw &&
      file?.raw.size > this.maxSize &&
      typeof this.showSizeExceedErrMsgFn === "function" &&
      getFileSizeWithUnit(file?.raw.size) !== getFileSizeWithUnit(this.maxSize)
    ) {
      this.showSizeExceedErrMsgFn();
      removeFile();
      return false;
    }
    this.triggerChange();
    if (this.autoUpload) {
      this.upload();
    }
  }
  triggerChange() {
    this.emitingChange = true;
    this.$emit("change", this.getUploadFilesUrl());
    setTimeout(() => {
      this.emitingChange = false;
    });
  }
  getUploadFilesUrl(): string[] {
    return this.elUploader.uploadFiles.map((ele) => {
      return this.getRemoteUrl(ele) || ele.url;
    });
  }
  checkTokenFresh() {
    const time = Date.now();
    const fetchedDate = this.tokenLastFetch as Date;
    const expire = 1 * 60 * 60 * 1000; //1小时过期
    return time - fetchedDate.getTime() < expire;
  }
  async beforeUpload() {
    const isTokenExpire = !this.checkTokenFresh();
    if (isTokenExpire) {
      const res = await this.getToken().catch(() => false);
      if (!res) {
        return false;
      }
    }
    return true;
  }
  upload() {
    if (!this.token) {
      this.$message.warning("上传组件未就绪，请稍后重试");
      return;
    }
    this.elUploader.submit();
  }
  isUploadSuccessAll() {
    return this.elUploader.uploadFiles.every(
      (uf) => uf.status === "success" && !!this.getRemoteUrl(uf)
    );
  }
  getFilesInfo(): FileInfo[] {
    return this.elUploader.uploadFiles.map((ele, index) => ({
      index,
      file: ele.raw as File,
      url: this.getRemoteUrl(ele),
      status: ele.status,
    }));
  }
}
